import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const DAYS_OF_WEEK = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

const AcaryaSlots = () => {
  const [availability, setAvailability] = useState([]);
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [newSlot, setNewSlot] = useState({ start: '09:00', end: '13:00' });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/acaryas/me/profile');
      if (res.data.success) {
        // Initialize availability if empty or malformed
        const av = res.data.data.availability || [];
        // Ensure all days exist
        const formatted = DAYS_OF_WEEK.map(day => {
          const found = av.find(a => a.day === day);
          return {
            day,
            slots: found ? found.slots : []
          };
        });
        setAvailability(formatted);
      }
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to load scheduling slots.');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const handleAddSlot = (e) => {
    e.preventDefault();
    if (!newSlot.start || !newSlot.end) return;
    if (newSlot.start >= newSlot.end) {
      showToast('error', 'End time must be after start time.');
      return;
    }

    setAvailability(prev => prev.map(a => {
      if (a.day === selectedDay) {
        // Prevent duplicate/overlapping slots roughly (can improve later)
        const exists = a.slots.some(s => s.start === newSlot.start && s.end === newSlot.end);
        if (exists) return a;
        return {
          ...a,
          slots: [...a.slots, { start: newSlot.start, end: newSlot.end }].sort((x, y) => x.start.localeCompare(y.start))
        };
      }
      return a;
    }));
    showToast('success', `Added slot for ${selectedDay}. Remember to Save Changes!`);
  };

  const handleRemoveSlot = (day, index) => {
    setAvailability(prev => prev.map(a => {
      if (a.day === day) {
        return {
          ...a,
          slots: a.slots.filter((_, idx) => idx !== index)
        };
      }
      return a;
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const res = await api.put('/acaryas/me/availability', { availability });
      if (res.data.success) {
        showToast('success', '✅ Availability schedule saved successfully!');
      }
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Failed to save availability.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && availability.length === 0) return (
    <div className="flex justify-center py-20 bg-[#0E0E26] min-h-screen">
      <div className="w-12 h-12 border-4 border-[#E8B84B]/20 border-t-[#E8B84B] rounded-full animate-spin"></div>
    </div>
  );

  const activeDaySchedule = availability.find(a => a.day === selectedDay) || { slots: [] };

  return (
    <div className="p-8 bg-[#0E0E26] min-h-screen text-white font-sans">
      
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-black text-white uppercase tracking-widest">Availability Scheduler</h2>
          <p className="text-white/40 text-xs font-bold uppercase tracking-widest mt-2">Manage open days and consultation hours</p>
        </div>
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-6 py-3 bg-[#E8B84B] text-black font-black text-xs uppercase tracking-widest rounded-xl hover:bg-white active:scale-95 transition-all shadow-xl disabled:opacity-50"
        >
          {loading ? 'Saving...' : '💾 Save Schedule'}
        </button>
      </div>

      {toast && (
        <div className={`p-4 rounded-xl border mb-6 text-xs font-bold ${toast.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
          {toast.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left column: Days of the week selection */}
        <div className="bg-[#14122B] p-6 rounded-3xl border border-white/5 shadow-2xl">
          <h3 className="text-sm font-black uppercase text-white/40 tracking-wider mb-4">Select Day</h3>
          <div className="space-y-2">
            {DAYS_OF_WEEK.map(day => {
              const count = availability.find(a => a.day === day)?.slots?.length || 0;
              const isSelected = selectedDay === day;
              return (
                <button
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  className={`w-full p-4 rounded-2xl border text-left flex justify-between items-center transition-all ${isSelected ? 'bg-[#E8B84B]/15 border-[#E8B84B] text-white' : 'bg-black/20 border-white/5 text-white/60 hover:bg-white/5'}`}
                >
                  <span className="text-sm font-bold">{day}</span>
                  <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${count > 0 ? 'bg-[#E8B84B]/20 text-[#E8B84B]' : 'bg-white/5 text-white/20'}`}>
                    {count} {count === 1 ? 'slot' : 'slots'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Middle column: Scheduler slots */}
        <div className="lg:col-span-2 bg-[#14122B] p-8 rounded-3xl border border-white/5 shadow-2xl flex flex-col justify-between">
          <div>
            <h3 className="text-base font-black text-white mb-2 uppercase tracking-wide">
              Slots for {selectedDay}
            </h3>
            <p className="text-white/40 text-xs mb-6">Manage available hours for seekers to call or chat with you.</p>

            {/* List current slots */}
            <div className="space-y-3 mb-8">
              {activeDaySchedule.slots.length === 0 ? (
                <div className="p-8 text-center bg-black/20 border border-dashed border-white/5 rounded-2xl text-white/20 text-xs font-bold uppercase tracking-wider">
                  No slots defined. You will be offline for consultations on {selectedDay}.
                </div>
              ) : (
                activeDaySchedule.slots.map((slot, index) => (
                  <div key={index} className="p-4 bg-black/20 border border-white/5 rounded-2xl flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-[#E8B84B]">schedule</span>
                      <span className="text-sm font-black text-white/80">{slot.start} — {slot.end}</span>
                    </div>
                    <button
                      onClick={() => handleRemoveSlot(selectedDay, index)}
                      className="w-8 h-8 rounded-xl bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white transition-all flex items-center justify-center"
                    >
                      <span className="material-symbols-outlined text-sm">delete</span>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Add a new slot form */}
          <form onSubmit={handleAddSlot} className="p-5 bg-black/20 border border-white/5 rounded-2xl">
            <h4 className="text-xs font-black uppercase text-white/60 tracking-wider mb-4">Add Consultation Slot</h4>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-[10px] text-white/40 font-bold uppercase mb-1">Start Time</label>
                <input
                  type="time"
                  required
                  value={newSlot.start}
                  onChange={e => setNewSlot({ ...newSlot, start: e.target.value })}
                  className="w-full bg-[#0E0E26] border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[#E8B84B]/50"
                />
              </div>
              <div>
                <label className="block text-[10px] text-white/40 font-bold uppercase mb-1">End Time</label>
                <input
                  type="time"
                  required
                  value={newSlot.end}
                  onChange={e => setNewSlot({ ...newSlot, end: e.target.value })}
                  className="w-full bg-[#0E0E26] border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[#E8B84B]/50"
                />
              </div>
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-white/5 border border-white/10 text-white font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-[#E8B84B] hover:text-black transition-all"
            >
              Add Time Slot
            </button>
          </form>

        </div>

      </div>

    </div>
  );
};

export default AcaryaSlots;
